import {
    useCallback, useMemo,
} from 'react'
import {
    TypeWithId,
} from 'app/Apps/Pricing/ContractBasis/utils/createQuote.types'
import {
    RequestKeys,
} from 'app/hooks/useRequest'
import useDataGridSource from 'app/PoC/DataGridDemoPages/components/hooks/useDataGridSource'
import SkyNetDataGrid from 'app/PoC/DataGridDemoPages/components/SkyNetDataGrid'
import {
    SelectionType,
} from 'app/PoC/DataGridDemoPages/components/SkyNetDataGrid/SkyNetDataGrid'
import ContractBasisRequests from 'app/requests/ContractBasis/ContractBasis.requests'
import useLabel from 'app/hooks/useLabel'

import additionalFeesColumns from './additionalFees.columns'
import useStyles from './DemoAddFeesLaneGroupsForm.style'
import laneGroupColumns from './laneGroups.columns'

export default function DemoAddFeesLaneGroupsForm({
    disabled,
    id,
    setAdditionalFee,
    setLaneGroups,
    quoteId,
}: Readonly<{
    disabled: boolean,
    id: number,
    setAdditionalFee: (id: TypeWithId) => void,
    setLaneGroups: (laneGroups: TypeWithId[]) => void,
    quoteId?: number,
}
>) {
    const getLabel = useLabel()
    const {
        classes,
    } = useStyles()
    const additionalFeesRequest = useMemo(() => {
        return ContractBasisRequests.filterRequestAdditionalFees({
            contractId: id,
            params: {},
        })
    }, [id])
    const additionalFeesDataSource = useDataGridSource({
        request: additionalFeesRequest,
        key: RequestKeys.getContractBasisAdditionalFee,
    })

    const laneGroupRequest = useMemo(() => {
        return ContractBasisRequests.filterRequestLaneGroups({
            contractId: id,
            quoteId,
        })
    }, [
        id,
        quoteId,
    ])

    const laneGroupDataSource = useDataGridSource({
        request: laneGroupRequest,
        key: RequestKeys.getContractBasisLaneGroups,
        rowsPerPage: 200,
    })

    const handleSetSelectedAdditionalFeesIds = useCallback((selectedIds: Set<number>) => {
        const [additionFeeId] = Array.from(selectedIds)

        setAdditionalFee({
            id: additionFeeId,
        })
    }, [setAdditionalFee])

    const handleSetSelectedLaneGroupsIds = useCallback((selectedIds: Set<number>) => {
        setLaneGroups(Array.from(selectedIds).map((laneGroupId) => {
            return {
                id: laneGroupId,
            }
        }))
    }, [setLaneGroups])

    return (
        <div>
            <div
                className={classes.cardContent}
                data-testid="createquote-additionalFees-card"
            >
                <SkyNetDataGrid
                    persistentKey="demo-contract-basis-createquote-additional-fees-table"
                    columns={additionalFeesColumns}
                    customDataSource={additionalFeesDataSource}
                    toolbarProps={{
                        title: getLabel('ADDITIONAL_FEES'),
                        toolbarControls: [],
                    }}
                    selectionType={SelectionType.Radio}
                    onSelectedRow={handleSetSelectedAdditionalFeesIds}
                    disabled={disabled}
                    classNames={{
                        tableWrapper: classes.tableWrapper,
                    }}
                />
            </div>
            <div
                className={classes.cardContent}
                data-testid="createquote-laneGroup-card"
            >
                <SkyNetDataGrid
                    persistentKey="demo-contract-basis-createquote-lanegroups-table"
                    columns={laneGroupColumns}
                    customDataSource={laneGroupDataSource}
                    toolbarProps={{
                        title: getLabel('LANE_GROUPS'),
                        toolbarControls: [],
                    }}
                    selectionType={SelectionType.Checkbox}
                    onSelectedRow={handleSetSelectedLaneGroupsIds}
                    disabled={disabled}
                    classNames={{
                        tableWrapper: classes.tableWrapper,
                    }}
                />
            </div>
        </div>
    )
}
